import { List } from 'immutable';
import { ProjectGroup } from 'records/ProjectGroup';
import { AppState, AppStateActionParams } from 'records/AppState';
import {
  getProjectGroups,
  createProjectGroup,
  editProjectGroup,
  deleteProjectGroup,
  addUserToProjectGroup,
  removeUserFromProjectGroup,
  batchEditProjectGroups,
} from 'services/groupService';
import { BatchProjectGroupsEdit } from 'records/BatchProjectGroupsEdit';
import { getGroupsByMember, alphabetizeGroups } from 'utils/groups';

export interface GroupActions {
  getProjectGroups: (actionParams: AppStateActionParams) => Promise<AppState>;

  createProjectGroup: (
    actionParams: AppStateActionParams,
    name: string
  ) => Promise<AppState>;

  editProjectGroup: (
    actionParams: AppStateActionParams,
    projectid: string,
    groupid: string,
    name: string
  ) => Promise<AppState>;

  deleteProjectGroup: (
    actionParams: AppStateActionParams,
    projectid: string,
    groupid: string
  ) => Promise<AppState>;

  addUserToProjectGroup: (
    actionParams: AppStateActionParams,
    projectid: string,
    groupid: string,
    userid: string
  ) => Promise<AppState>;

  removeUserFromProjectGroup: (
    actionParams: AppStateActionParams,
    projectid: string,
    groupid: string,
    userid: string
  ) => Promise<AppState>;

  batchEditProjectGroups: (
    actionParams: AppStateActionParams,
    input: BatchProjectGroupsEdit
  ) => Promise<AppState>;
}

const groupActions: GroupActions = {
  getProjectGroups: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();
    const groupsResponse = await getProjectGroups(selectedProject, 1000);
    const groups =
      groupsResponse !== undefined
        ? groupsResponse.groups.filter((group) => group.name !== '')
        : List<ProjectGroup>();

    const sortedGroups = alphabetizeGroups(List(groups));
    const membersInGroups = getGroupsByMember(sortedGroups);

    return state()
      .setIn(['projectGroups'], sortedGroups)
      .setIn(['groupsByMember'], membersInGroups);
  },

  createProjectGroup: async (
    { state }: AppStateActionParams,
    name: string
  ): Promise<AppState> => {
    const { selectedProject } = state();
    const newGroup = await createProjectGroup(selectedProject, name);
    const { projectGroups } = state().mergeIn(['projectGroups'], newGroup);
    const sortedGroups = alphabetizeGroups(List(projectGroups));
    const membersInGroups = getGroupsByMember(sortedGroups);

    return state()
      .setIn(['projectGroups'], sortedGroups)
      .setIn(['groupsByMember'], membersInGroups);
  },

  editProjectGroup: async (
    { state }: AppStateActionParams,
    projectid: string,
    groupid: string,
    name: string
  ): Promise<AppState> => {
    const updatedProject = await editProjectGroup(projectid, groupid, name);

    return state().update(
      'projectGroups',
      (projectGroups): List<ProjectGroup> => {
        const index = projectGroups.findIndex(
          (group): boolean => group.groupid === groupid
        );

        return projectGroups.set(index, updatedProject);
      }
    );
  },

  deleteProjectGroup: async (
    { state }: AppStateActionParams,
    projectid: string,
    groupid: string
  ): Promise<AppState> => {
    await deleteProjectGroup(projectid, groupid);
    const groupToDelete = state().projectGroups.findIndex(
      (group) => group.groupid === groupid
    );
    const updatedGroups = state().projectGroups.delete(groupToDelete);
    return state().setIn(['projectGroups'], updatedGroups);
  },

  addUserToProjectGroup: async (
    { state }: AppStateActionParams,
    projectid: string,
    groupid: string,
    userid: string
  ): Promise<AppState> => {
    const updatedProject = await addUserToProjectGroup(
      projectid,
      groupid,
      userid
    );

    return state().setIn(['projectGroups'], updatedProject);
  },

  removeUserFromProjectGroup: async (
    { state }: AppStateActionParams,
    projectid: string,
    groupid: string,
    userid: string
  ): Promise<AppState> => {
    await removeUserFromProjectGroup(projectid, groupid, userid);
    return state();
  },

  batchEditProjectGroups: async (
    { state }: AppStateActionParams,
    input: BatchProjectGroupsEdit
  ): Promise<AppState> => {
    const updatedProjectGroups = await batchEditProjectGroups(input);
    const sortedGroups = alphabetizeGroups(List(updatedProjectGroups));
    const membersInGroups = getGroupsByMember(sortedGroups);
    return state()
      .setIn(['projectGroups'], updatedProjectGroups)
      .setIn(['groupsByMember'], membersInGroups);
  },
};

export default groupActions;
